"use client";

import { notifyMetricsPageEmail } from "@/lib/notifications/public-notify-actions";
import { getCurrentUser } from "@/lib/db/users";
import { createClient } from "@/utils/supabase/client";
import { motion } from "framer-motion";
import {
  AlertCircle,
  Briefcase,
  CalendarDays,
  ChefHat,
  Clock,
  DollarSign,
  Download,
  FileText,
  Mail,
  MapPin,
  MessageSquare,
  Sparkles,
  Star,
  TrendingUp,
  Users,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Area,
  AreaChart,
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { CTA } from "../../shared/components/CTA";

// Mock data for demonstration
const mockOverviewStats = {
  totalAttendees: 8452,
  totalEvents: 47,
  destinationsVisited: 23,
  averageRating: 4.7,
  totalBudgetManaged: 2847000,
  averageAttendance: 180,
  repeatAttendeeRate: 68,
  netPromoterScore: 72,
};

const mockEventTimeline = [
  { month: "Jan", past: 4, live: 1, upcoming: 2 },
  { month: "Feb", past: 5, live: 0, upcoming: 3 },
  { month: "Mar", past: 6, live: 2, upcoming: 4 },
  { month: "Apr", past: 8, live: 1, upcoming: 5 },
  { month: "May", past: 10, live: 0, upcoming: 6 },
  { month: "Jun", past: 12, live: 3, upcoming: 8 },
];

const mockAttendeeGrowth = [
  { month: "Jan", attendees: 450, target: 400 },
  { month: "Feb", attendees: 680, target: 600 },
  { month: "Mar", attendees: 820, target: 750 },
  { month: "Apr", attendees: 850, target: 900 },
  { month: "May", attendees: 1100, target: 1000 },
  { month: "Jun", attendees: 1350, target: 1200 },
];

const mockEventTypes = [
  { name: "Conferences", value: 35, color: "#6366F1" },
  { name: "Team Building", value: 25, color: "#8B5CF6" },
  { name: "Product Launches", value: 20, color: "#A855F7" },
  { name: "Executive Retreats", value: 12, color: "#EC4899" },
  { name: "Holiday Parties", value: 8, color: "#F472B6" },
];

const mockDestinations = [
  { city: "San Francisco", events: 12, rating: 4.8 },
  { city: "New York", events: 8, rating: 4.6 },
  { city: "Austin", events: 6, rating: 4.9 },
  { city: "Miami", events: 5, rating: 4.7 },
  { city: "Las Vegas", events: 4, rating: 4.5 },
  { city: "Chicago", events: 4, rating: 4.6 },
  { city: "Boston", events: 3, rating: 4.7 },
  { city: "Seattle", events: 3, rating: 4.8 },
];

const mockROIData = [
  { event: "Q1 Sales Kickoff", investment: 125000, return: 450000, roi: 260 },
  { event: "Product Launch NYC", investment: 85000, return: 320000, roi: 276 },
  { event: "Team Summit 2024", investment: 65000, return: 150000, roi: 131 },
  { event: "Customer Conference", investment: 200000, return: 680000, roi: 240 },
  { event: "Holiday Gala", investment: 45000, return: 75000, roi: 67 },
];

const mockSentimentData = [
  { aspect: "Venue Quality", score: 92 },
  { aspect: "Food & Beverage", score: 88 },
  { aspect: "Content Quality", score: 95 },
  { aspect: "Networking", score: 85 },
  { aspect: "Organization", score: 91 },
  { aspect: "Value for Time", score: 87 },
];

const mockRecentEvents = [
  {
    name: "Annual Sales Kickoff 2024",
    date: "June 15-17, 2024",
    location: "Las Vegas, NV",
    attendees: 320,
    satisfaction: 4.8,
    formResponses: 285,
    responseRate: 89,
    status: "completed",
  },
  {
    name: "Product Innovation Summit",
    date: "July 8-9, 2024",
    location: "San Francisco, CA",
    attendees: 150,
    satisfaction: 4.9,
    formResponses: 142,
    responseRate: 95,
    status: "live",
  },
  {
    name: "Customer Success Conference",
    date: "July 22-24, 2024",
    location: "Austin, TX",
    attendees: 450,
    satisfaction: null,
    formResponses: 0,
    responseRate: 0,
    status: "upcoming",
  },
];

// AI Chat Demo Data
const aiChatPrompts = [
  {
    id: 1,
    icon: <ChefHat className="h-5 w-5" />,
    prompt: "How many attendees have dietary restrictions and what are they?",
    response: {
      summary: "42 attendees (13.1%) have reported dietary restrictions",
      data: [
        { restriction: "Vegetarian", count: 18, percentage: "5.6%" },
        { restriction: "Vegan", count: 8, percentage: "2.5%" },
        { restriction: "Gluten-Free", count: 7, percentage: "2.2%" },
        { restriction: "Kosher", count: 5, percentage: "1.6%" },
        { restriction: "Nut Allergy", count: 3, percentage: "0.9%" },
        { restriction: "Halal", count: 1, percentage: "0.3%" },
      ],
    },
  },
  {
    id: 2,
    icon: <Briefcase className="h-5 w-5" />,
    prompt: "What is the breakdown of role types attending this event?",
    response: {
      summary: "320 attendees across 6 main role categories",
      data: [
        { role: "Sales Representatives", count: 112, percentage: "35%", department: "Sales" },
        { role: "Account Executives", count: 64, percentage: "20%", department: "Sales" },
        { role: "Sales Managers", count: 48, percentage: "15%", department: "Sales" },
        { role: "Customer Success", count: 42, percentage: "13%", department: "CS" },
        { role: "Marketing", count: 32, percentage: "10%", department: "Marketing" },
        { role: "Leadership", count: 22, percentage: "7%", department: "Executive" },
      ],
    },
  },
  {
    id: 3,
    icon: <AlertCircle className="h-5 w-5" />,
    prompt: "What special accommodations have been requested?",
    response: {
      summary: "27 special accommodation requests from attendees",
      data: [
        { type: "Wheelchair Accessibility", count: 8, status: "Confirmed", notes: "All venues verified" },
        { type: "Quiet Room Access", count: 6, status: "Arranged", notes: "Room 205 designated" },
        { type: "Sign Language Interpreter", count: 4, status: "Booked", notes: "For keynote sessions" },
        { type: "Large Print Materials", count: 3, status: "Prepared", notes: "Available at registration" },
        { type: "Service Animal", count: 3, status: "Approved", notes: "Hotel notified" },
        { type: "Prayer Room", count: 2, status: "Arranged", notes: "Room 108 available" },
        { type: "Nursing Room", count: 1, status: "Confirmed", notes: "Private room secured" },
      ],
    },
  },
];

export default function MetricsPage() {
  const supabase = createClient();

  const [selectedTimeRange, setSelectedTimeRange] = useState("6months");
  const [selectedMetric, setSelectedMetric] = useState("overview");
  const [isLoggedIn, setIsLoggedIn] = useState<boolean | null>(null);
  const [email, setEmail] = useState("");
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const [selectedAIPrompt, setSelectedAIPrompt] = useState<number | null>(null);
  const [showAIResponse, setShowAIResponse] = useState(false);
  const [streamedText, setStreamedText] = useState("");
  const [isStreaming, setIsStreaming] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      const user = await getCurrentUser(supabase);
      setIsLoggedIn(!!user);
    };
    checkAuth();
  }, [supabase]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5,
      },
    },
  };

  const handleAIPromptClick = async (promptId: number) => {
    setSelectedAIPrompt(promptId);
    setShowAIResponse(false);
    setStreamedText("");
    setIsStreaming(true);

    // Find the selected prompt data
    const selectedPromptData = aiChatPrompts.find((p) => p.id === promptId);
    if (!selectedPromptData) return;

    // Wait a bit before starting to show response
    await new Promise((resolve) => setTimeout(resolve, 500));
    setShowAIResponse(true);

    // Stream the summary text
    const summaryText = selectedPromptData.response.summary;
    let currentText = "";

    for (let i = 0; i <= summaryText.length; i++) {
      currentText = summaryText.slice(0, i);
      setStreamedText(currentText);
      await new Promise((resolve) => setTimeout(resolve, 20)); // Adjust speed here
    }

    // Small delay before showing the table
    await new Promise((resolve) => setTimeout(resolve, 300));
    setIsStreaming(false);
  };

  const downloadCSV = (content: string, filename: string) => {
    const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleDownloadOverview = () => {
    let csvContent = "";
    csvContent += "OVERVIEW STATISTICS & TIMELINE\n";
    csvContent += `Generated on: ${new Date().toLocaleString()}\n\n`;

    // Overview Stats
    csvContent += "KEY METRICS\n";
    csvContent += "Metric,Value,Growth\n";
    csvContent += `Total Attendees,${mockOverviewStats.totalAttendees},+12.5%\n`;
    csvContent += `Total Events,${mockOverviewStats.totalEvents},+8.3%\n`;
    csvContent += `Destinations Visited,${mockOverviewStats.destinationsVisited},+5\n`;
    csvContent += `Average Rating,${mockOverviewStats.averageRating},+0.3\n\n`;

    // Event Timeline
    csvContent += "EVENT TIMELINE BY MONTH\n";
    csvContent += "Month,Past Events,Live Events,Upcoming Events,Total\n";
    mockEventTimeline.forEach((row) => {
      csvContent += `${row.month},${row.past},${row.live},${row.upcoming},${row.past + row.live + row.upcoming}\n`;
    });
    csvContent += "\n";

    // Event Types
    csvContent += "EVENT TYPES DISTRIBUTION\n";
    csvContent += "Type,Count,Percentage\n";
    const totalEvents = mockEventTypes.reduce((sum, e) => sum + e.value, 0);
    mockEventTypes.forEach((row) => {
      csvContent += `${row.name},${row.value},${((row.value / totalEvents) * 100).toFixed(1)}%\n`;
    });
    csvContent += "\n";

    // Destinations
    csvContent += "TOP EVENT DESTINATIONS\n";
    csvContent += "Rank,City,Number of Events,Average Rating\n";
    mockDestinations.forEach((row, index) => {
      csvContent += `${index + 1},${row.city},${row.events},${row.rating}\n`;
    });
    csvContent += "\n";

    // Recent Events
    csvContent += "RECENT EVENTS\n";
    csvContent += "Event Name,Date,Location,Attendees,Satisfaction,Form Responses,Response Rate,Status\n";
    mockRecentEvents.forEach((event) => {
      csvContent += `"${event.name}","${event.date}","${event.location}",${event.attendees},${event.satisfaction || "N/A"},${event.formResponses},${event.responseRate}%,${event.status}\n`;
    });

    downloadCSV(csvContent, `event-overview-${new Date().toISOString().split("T")[0]}.csv`);
  };

  const handleDownloadPerformance = () => {
    let csvContent = "";
    csvContent += "PERFORMANCE METRICS & BUDGET ANALYSIS\n";
    csvContent += `Generated on: ${new Date().toLocaleString()}\n\n`;

    // Overview Stats
    csvContent += "KEY PERFORMANCE INDICATORS\n";
    csvContent += "Metric,Value,Growth\n";
    csvContent += `Total Attendees,${mockOverviewStats.totalAttendees},+12.5%\n`;
    csvContent += `Total Events,${mockOverviewStats.totalEvents},+8.3%\n`;
    csvContent += `Average Attendance,${mockOverviewStats.averageAttendance},+15%\n`;
    csvContent += `Repeat Attendee Rate,${mockOverviewStats.repeatAttendeeRate}%,+8%\n`;
    csvContent += `Net Promoter Score,${mockOverviewStats.netPromoterScore},+5\n\n`;

    // Attendee Growth
    csvContent += "ATTENDEE GROWTH VS TARGET\n";
    csvContent += "Month,Actual Attendees,Target,Difference,% of Target\n";
    mockAttendeeGrowth.forEach((row) => {
      const diff = row.attendees - row.target;
      const pct = ((row.attendees / row.target) * 100).toFixed(1);
      csvContent += `${row.month},${row.attendees},${row.target},${diff > 0 ? "+" : ""}${diff},${pct}%\n`;
    });
    csvContent += "\n";

    // Performance Metrics
    csvContent += "DETAILED PERFORMANCE METRICS\n";
    csvContent += "Metric,Value,YoY Change,Benchmark\n";
    csvContent += `Average Attendance Rate,${mockOverviewStats.averageAttendance},+15%,85%\n`;
    csvContent += `Repeat Attendee Rate,${mockOverviewStats.repeatAttendeeRate}%,+8%,68%\n`;
    csvContent += `Net Promoter Score,${mockOverviewStats.netPromoterScore},+5,72%\n\n`;

    // Budget Breakdown
    csvContent += "BUDGET ALLOCATION BREAKDOWN\n";
    csvContent += "Category,Percentage,Amount\n";
    const totalBudget = mockOverviewStats.totalBudgetManaged;
    csvContent += `Venue & Catering,42%,$${(totalBudget * 0.42).toLocaleString()}\n`;
    csvContent += `Travel & Accommodation,28%,$${(totalBudget * 0.28).toLocaleString()}\n`;
    csvContent += `Entertainment & Activities,18%,$${(totalBudget * 0.18).toLocaleString()}\n`;
    csvContent += `Marketing & Promotion,12%,$${(totalBudget * 0.12).toLocaleString()}\n`;
    csvContent += `Total Budget Managed,100%,$${totalBudget.toLocaleString()}\n`;

    downloadCSV(csvContent, `event-performance-${new Date().toISOString().split("T")[0]}.csv`);
  };

  const handleDownloadInsights = () => {
    let csvContent = "";
    csvContent += "POST-EVENT INSIGHTS & SENTIMENT ANALYSIS\n";
    csvContent += `Generated on: ${new Date().toLocaleString()}\n\n`;

    // Sentiment Analysis
    csvContent += "SENTIMENT ANALYSIS\n";
    csvContent += "Aspect,Satisfaction Score (out of 100)\n";
    mockSentimentData.forEach((row) => {
      csvContent += `${row.aspect},${row.score}\n`;
    });
    csvContent += "\n";

    // Engagement Metrics
    csvContent += "ENGAGEMENT METRICS\n";
    csvContent += "Metric,Value\n";
    csvContent += "Average Event Duration,6.2 hours\n";
    csvContent += "Session Attendance Rate,84%\n";
    csvContent += "Survey Response Rate,76%\n";
    csvContent += "Would Recommend,93%\n\n";

    // Top Feedback Themes
    csvContent += "TOP FEEDBACK THEMES\n";
    csvContent += "Theme,Rating,Details\n";
    csvContent += "Exceptional Networking,92%,92% of attendees valued networking opportunities\n";
    csvContent += "Content Quality,4.8/5,Speaker sessions rated 4.8/5 on average\n";
    csvContent += "Venue Selection,88%,88% satisfaction with venue choices\n\n";

    // Improvement Areas
    csvContent += "AREAS FOR IMPROVEMENT\n";
    csvContent += "Area,Feedback,Priority\n";
    csvContent += "Registration Process,Streamline check-in for large events,High\n";
    csvContent += "Mobile App Experience,Enhanced features requested by 45% of users,Medium\n";
    csvContent += "Follow-up Communication,More post-event resources needed,Medium\n";

    downloadCSV(csvContent, `event-insights-${new Date().toISOString().split("T")[0]}.csv`);
  };

  const handleDownloadAI = () => {
    let csvContent = "";
    csvContent += "AI ANALYTICS - ATTENDEE DATA ANALYSIS\n";
    csvContent += `Generated on: ${new Date().toLocaleString()}\n\n`;

    // Dietary Restrictions
    csvContent += "DIETARY RESTRICTIONS ANALYSIS\n";
    csvContent += "Restriction Type,Count,Percentage of Total Attendees\n";
    const dietaryData = aiChatPrompts[0].response.data as Array<{
      restriction: string;
      count: number;
      percentage: string;
    }>;
    dietaryData.forEach((row) => {
      csvContent += `${row.restriction},${row.count},${row.percentage}\n`;
    });
    csvContent += `Total with Restrictions,42,13.1%\n\n`;

    // Role Breakdown
    csvContent += "ATTENDEE ROLE BREAKDOWN\n";
    csvContent += "Role,Count,Percentage,Department\n";
    const roleData = aiChatPrompts[1].response.data as Array<{
      role: string;
      count: number;
      percentage: string;
      department: string;
    }>;
    roleData.forEach((row) => {
      csvContent += `${row.role},${row.count},${row.percentage},${row.department}\n`;
    });
    csvContent += `Total Attendees,320,100%,All\n\n`;

    // Special Accommodations
    csvContent += "SPECIAL ACCOMMODATIONS REQUESTED\n";
    csvContent += "Accommodation Type,Count,Status,Notes\n";
    const accommodationData = aiChatPrompts[2].response.data as Array<{
      type: string;
      count: number;
      status: string;
      notes: string;
    }>;
    accommodationData.forEach((row) => {
      csvContent += `"${row.type}",${row.count},${row.status},"${row.notes}"\n`;
    });
    csvContent += `Total Requests,27,N/A,All accommodations confirmed\n\n`;

    // Quick Stats
    csvContent += "QUICK STATISTICS\n";
    csvContent += "Metric,Value\n";
    csvContent += "Response Rate,89%\n";
    csvContent += "Special Requests,27\n";
    csvContent += "Dietary Restrictions,42\n";
    csvContent += "First-Time Attendees,38%\n";

    downloadCSV(csvContent, `event-ai-analytics-${new Date().toISOString().split("T")[0]}.csv`);
  };

  const handleDownloadROI = () => {
    let csvContent = "";
    csvContent += "ROI ANALYSIS & FINANCIAL METRICS\n";
    csvContent += `Generated on: ${new Date().toLocaleString()}\n\n`;

    // ROI Analysis
    csvContent += "EVENT ROI BREAKDOWN\n";
    csvContent += "Event Name,Investment Amount,Revenue Generated,ROI Percentage,Net Return\n";
    mockROIData.forEach((row) => {
      const netReturn = row.return - row.investment;
      csvContent += `"${row.event}",$${row.investment.toLocaleString()},$${row.return.toLocaleString()},${row.roi}%,$${netReturn.toLocaleString()}\n`;
    });
    csvContent += "\n";

    // ROI Summary
    csvContent += "ROI SUMMARY METRICS\n";
    csvContent += "Metric,Value\n";
    const avgROI = mockROIData.reduce((sum, e) => sum + e.roi, 0) / mockROIData.length;
    const totalInvestment = mockROIData.reduce((sum, e) => sum + e.investment, 0);
    const totalReturn = mockROIData.reduce((sum, e) => sum + e.return, 0);
    const totalNetReturn = totalReturn - totalInvestment;
    csvContent += `Average ROI,${avgROI.toFixed(0)}%\n`;
    csvContent += `Total Investment,$${totalInvestment.toLocaleString()}\n`;
    csvContent += `Total Return,$${totalReturn.toLocaleString()}\n`;
    csvContent += `Total Net Return,$${totalNetReturn.toLocaleString()}\n`;
    csvContent += `Pipeline Generated,$4.2M\n`;
    csvContent += `Cost per Lead,$127\n`;
    csvContent += `Below Industry Average,32%\n\n`;

    // Best and Worst Performing
    const sortedByROI = [...mockROIData].sort((a, b) => b.roi - a.roi);
    csvContent += "TOP PERFORMING EVENTS\n";
    csvContent += "Rank,Event,ROI %\n";
    sortedByROI.slice(0, 3).forEach((event, index) => {
      csvContent += `${index + 1},"${event.event}",${event.roi}%\n`;
    });

    downloadCSV(csvContent, `event-roi-analysis-${new Date().toISOString().split("T")[0]}.csv`);
  };

  return (
    <div className="min-h-screen bg-landing-primary">
      {/* Hero Section */}
      <motion.section
        className="bg-landing-primary pt-32 pb-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.6 }}
      >
        <div className="container-page">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-h1 font-bold text-landing-primary mb-4">Event Reporting & Analytics</h1>
            <p className="text-h4 text-landing-secondary mb-8">
              Track and measure your event performance across your organization
            </p>
            <p className="text-body text-landing-secondary mb-6">
              Get comprehensive insights into your event performance, attendee engagement, and ROI across your entire
              organization
            </p>
          </div>
        </div>
      </motion.section>

      {/* Email Collection Form for Non-Logged Users */}
      {isLoggedIn === false && !emailSubmitted && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="max-w-md mx-auto px-4 py-8"
        >
          <div className="bg-white rounded-2xl shadow-lg p-8 border border-indigo-100 mb-16">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="h-8 w-8 text-indigo-600" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Unlock Sample Event Analytics Dashboard</h2>
              <p className="text-gray-600">
                Submit your email to access metrics and performance insights for a sample event
              </p>
            </div>
            <form
              onSubmit={async (e) => {
                e.preventDefault();
                setEmailSubmitted(true);

                // Save to database
                try {
                  const insertData = {
                    email: email,
                    origin: "metrics",
                    created_at: new Date().toISOString(),
                  };

                  await supabase.schema("nowadays").from("landing_page_submissions").insert([insertData]);

                  // Send Slack notification
                  await notifyMetricsPageEmail({ email });
                } catch (error) {
                  console.error("Error saving email:", error);
                }

                // Show metrics after email submission
                setTimeout(() => setIsLoggedIn(true), 1000);
              }}
              className="space-y-4"
            >
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Work Email
                </label>
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full px-4 py-3 rounded-full border border-gray-300 focus:border-indigo-500 focus:ring-2 focus:ring-indigo-200 transition-all"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full px-6 py-3 bg-indigo-100 text-indigo-700 font-semibold rounded-full border-2 border-indigo-200 hover:bg-indigo-200 transition-all shadow-sm"
              >
                Get Free Access
              </button>
            </form>
          </div>
        </motion.div>
      )}

      {/* Success Message */}
      {emailSubmitted && isLoggedIn === false && (
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3 }}
          className="max-w-md mx-auto px-4 py-8"
        >
          <div className="bg-green-50 border-2 border-green-200 rounded-2xl p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="h-8 w-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900 mb-2">Thank You!</h3>
            <p className="text-gray-600">Loading sample event metrics dashboard...</p>
          </div>
        </motion.div>
      )}

      {/* Main Content - Only show when logged in */}
      {(isLoggedIn === true || (emailSubmitted && isLoggedIn !== false)) && (
        <div className="max-w-7xl mx-auto px-4 py-8 -mt-8 mb-16">
          {/* Time Range Selector */}
          <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
            <div className="flex gap-2 flex-wrap">
              <button
                onClick={() => setSelectedMetric("overview")}
                className={`px-6 py-3 rounded-full transition-all font-medium ${
                  selectedMetric === "overview"
                    ? "bg-indigo-100 text-indigo-700 border-2 border-indigo-200 shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                Overview
              </button>
              <button
                onClick={() => setSelectedMetric("performance")}
                className={`px-6 py-3 rounded-full transition-all font-medium ${
                  selectedMetric === "performance"
                    ? "bg-purple-100 text-purple-700 border-2 border-purple-200 shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                Performance
              </button>
              <button
                onClick={() => setSelectedMetric("insights")}
                className={`px-6 py-3 rounded-full transition-all font-medium ${
                  selectedMetric === "insights"
                    ? "bg-pink-100 text-pink-700 border-2 border-pink-200 shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                Insights
              </button>
              <button
                onClick={() => setSelectedMetric("ai")}
                className={`px-6 py-3 rounded-full transition-all font-medium ${
                  selectedMetric === "ai"
                    ? "bg-purple-100 text-purple-700 border-2 border-purple-200 shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                AI Chat
              </button>
              <button
                onClick={() => setSelectedMetric("roi")}
                className={`px-6 py-3 rounded-full transition-all font-medium ${
                  selectedMetric === "roi"
                    ? "bg-indigo-100 text-indigo-700 border-2 border-indigo-200 shadow-sm"
                    : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                }`}
              >
                ROI
              </button>
            </div>
            <button
              onClick={() => {
                if (selectedMetric === "overview") handleDownloadOverview();
                else if (selectedMetric === "performance") handleDownloadPerformance();
                else if (selectedMetric === "insights") handleDownloadInsights();
                else if (selectedMetric === "ai") handleDownloadAI();
                else if (selectedMetric === "roi") handleDownloadROI();
              }}
              className="flex items-center gap-2 px-6 py-3 bg-green-100 text-green-700 font-semibold rounded-full border-2 border-green-200 hover:bg-green-200 transition-all shadow-sm"
            >
              <Download className="h-5 w-5" />
              Download{" "}
              {selectedMetric === "ai"
                ? "AI Chat"
                : selectedMetric === "roi"
                  ? "ROI"
                  : selectedMetric.charAt(0).toUpperCase() + selectedMetric.slice(1)}{" "}
              CSV
            </button>
          </div>

          {/* Main Stats Grid */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
          >
            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <Users className="h-8 w-8 text-indigo-600" />
                <span className="text-sm text-green-600 font-medium">+12.5%</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{mockOverviewStats.totalAttendees.toLocaleString()}</h3>
              <p className="text-gray-600 text-sm mt-1">Total Attendees</p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <CalendarDays className="h-8 w-8 text-purple-600" />
                <span className="text-sm text-green-600 font-medium">+8.3%</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{mockOverviewStats.totalEvents}</h3>
              <p className="text-gray-600 text-sm mt-1">Events Organized</p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <MapPin className="h-8 w-8 text-pink-600" />
                <span className="text-sm text-green-600 font-medium">+5</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{mockOverviewStats.destinationsVisited}</h3>
              <p className="text-gray-600 text-sm mt-1">Destinations</p>
            </motion.div>

            <motion.div variants={itemVariants} className="bg-white rounded-xl shadow-sm p-6">
              <div className="flex items-center justify-between mb-4">
                <Star className="h-8 w-8 text-indigo-500" />
                <span className="text-sm text-green-600 font-medium">+0.3</span>
              </div>
              <h3 className="text-2xl font-bold text-gray-900">{mockOverviewStats.averageRating}</h3>
              <p className="text-gray-600 text-sm mt-1">Average Rating</p>
            </motion.div>
          </motion.div>

          {selectedMetric === "overview" && (
            <>
              {/* Event Timeline */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Timeline</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={mockEventTimeline}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                      <XAxis dataKey="month" />
                      <YAxis />
                      <Tooltip />
                      <Legend />
                      <Bar dataKey="past" stackId="a" fill="#C7D2FE" name="Past Events" />
                      <Bar dataKey="live" stackId="a" fill="#10B981" name="Live Events" />
                      <Bar dataKey="upcoming" stackId="a" fill="#818CF8" name="Upcoming Events" />
                    </BarChart>
                  </ResponsiveContainer>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Event Types Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={mockEventTypes}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={5}
                        dataKey="value"
                      >
                        {mockEventTypes.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                      <Legend />
                    </PieChart>
                  </ResponsiveContainer>
                </motion.div>
              </div>

              {/* Recent Events Table */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6 mb-8"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Recent Events Status</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Event Name</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Date</th>
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Location</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Attendees</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Form Responses</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockRecentEvents.map((event, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4">
                            <div className="font-medium text-gray-900">{event.name}</div>
                          </td>
                          <td className="py-3 px-4 text-sm text-gray-600">{event.date}</td>
                          <td className="py-3 px-4 text-sm text-gray-600">{event.location}</td>
                          <td className="py-3 px-4 text-center text-sm text-gray-900">{event.attendees}</td>
                          <td className="py-3 px-4 text-center">
                            <div className="text-sm text-gray-900">
                              {event.formResponses}/{event.attendees}
                            </div>
                            <div className="text-xs text-gray-500">{event.responseRate}% response rate</div>
                          </td>
                          <td className="py-3 px-4 text-center">
                            <span
                              className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                                event.status === "completed"
                                  ? "bg-gray-100 text-gray-700"
                                  : event.status === "live"
                                    ? "bg-green-100 text-green-700"
                                    : "bg-indigo-100 text-indigo-700"
                              }`}
                            >
                              {event.status === "completed"
                                ? "Completed"
                                : event.status === "live"
                                  ? "Live"
                                  : "Upcoming"}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>

              {/* Top Destinations */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Top Event Destinations</h3>
                <div className="space-y-4">
                  {mockDestinations.slice(0, 5).map((destination, index) => (
                    <div key={index} className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center text-sm font-medium text-indigo-600">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-medium text-gray-900">{destination.city}</div>
                          <div className="text-sm text-gray-500">{destination.events} events</div>
                        </div>
                      </div>
                      <div className="flex items-center gap-1">
                        <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                        <span className="text-sm font-medium text-gray-700">{destination.rating}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </motion.div>
            </>
          )}

          {selectedMetric === "performance" && (
            <>
              {/* Attendee Growth Chart */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6 mb-8"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Attendee Growth vs Target</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <AreaChart data={mockAttendeeGrowth}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#d1d5db" />
                    <XAxis dataKey="month" />
                    <YAxis />
                    <Tooltip />
                    <Legend />
                    <Area type="monotone" dataKey="target" stackId="1" stroke="#9ca3af" fill="#d1d5db" name="Target" />
                    <Area
                      type="monotone"
                      dataKey="attendees"
                      stackId="2"
                      stroke="#6366F1"
                      fill="#C7D2FE"
                      name="Actual"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Performance Metrics Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <TrendingUp className="h-6 w-6 text-green-600" />
                    <span className="text-xs text-gray-500">YoY</span>
                  </div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Average Attendance Rate</h4>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">{mockOverviewStats.averageAttendance}</span>
                    <span className="text-sm text-green-600">+15%</span>
                  </div>
                  <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-green-600 h-2 rounded-full" style={{ width: "85%" }}></div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <Users className="h-6 w-6 text-indigo-600" />
                    <span className="text-xs text-gray-500">YoY</span>
                  </div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Repeat Attendee Rate</h4>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">{mockOverviewStats.repeatAttendeeRate}%</span>
                    <span className="text-sm text-green-600">+8%</span>
                  </div>
                  <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-indigo-600 h-2 rounded-full" style={{ width: "68%" }}></div>
                  </div>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-white rounded-xl shadow-sm p-6"
                >
                  <div className="flex items-center justify-between mb-4">
                    <FileText className="h-6 w-6 text-purple-600" />
                    <span className="text-xs text-gray-500">NPS</span>
                  </div>
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Net Promoter Score</h4>
                  <div className="flex items-baseline gap-2">
                    <span className="text-2xl font-bold text-gray-900">{mockOverviewStats.netPromoterScore}</span>
                    <span className="text-sm text-green-600">+5</span>
                  </div>
                  <div className="mt-3 w-full bg-gray-200 rounded-full h-2">
                    <div className="bg-purple-600 h-2 rounded-full" style={{ width: "72%" }}></div>
                  </div>
                </motion.div>
              </div>

              {/* Budget Management */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Budget Management Overview</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-3">Total Budget Managed</h4>
                    <div className="text-3xl font-bold text-gray-900 mb-4">
                      ${(mockOverviewStats.totalBudgetManaged / 1000000).toFixed(2)}M
                    </div>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Venue & Catering</span>
                        <span className="text-sm font-medium">42%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-indigo-600 h-2 rounded-full" style={{ width: "42%" }}></div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Travel & Accommodation</span>
                        <span className="text-sm font-medium">28%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-purple-600 h-2 rounded-full" style={{ width: "28%" }}></div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Entertainment & Activities</span>
                        <span className="text-sm font-medium">18%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-pink-600 h-2 rounded-full" style={{ width: "18%" }}></div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm text-gray-600">Marketing & Promotion</span>
                        <span className="text-sm font-medium">12%</span>
                      </div>
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div className="bg-indigo-400 h-2 rounded-full" style={{ width: "12%" }}></div>
                      </div>
                    </div>
                  </div>
                  <div>
                    <h4 className="text-sm font-medium text-gray-600 mb-3">Cost per Attendee Trend</h4>
                    <ResponsiveContainer width="100%" height={250}>
                      <LineChart data={mockAttendeeGrowth}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="month" />
                        <YAxis />
                        <Tooltip />
                        <Line type="monotone" dataKey="target" stroke="#8B5CF6" strokeWidth={2} name="Cost/Attendee" />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </motion.div>
            </>
          )}

          {selectedMetric === "insights" && (
            <>
              {/* Sentiment Analysis */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6 mb-8"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Post-Event Attendee Sentiment Analysis</h3>
                <p className="text-sm text-gray-600 mb-6">AI-powered insights from attendee feedback surveys</p>
                <ResponsiveContainer width="100%" height={350}>
                  <RadarChart data={mockSentimentData}>
                    <PolarGrid stroke="#e5e7eb" />
                    <PolarAngleAxis dataKey="aspect" className="text-sm" />
                    <PolarRadiusAxis angle={90} domain={[0, 100]} />
                    <Radar
                      name="Satisfaction Score"
                      dataKey="score"
                      stroke="#8B5CF6"
                      fill="#DDD6FE"
                      fillOpacity={0.6}
                    />
                    <Tooltip />
                  </RadarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* Key Insights Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-indigo-50 to-purple-100 rounded-xl p-6"
                >
                  <h4 className="text-lg font-semibold text-indigo-900 mb-3">Top Feedback Themes</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Exceptional Networking</span>
                        <p className="text-sm text-gray-600">92% of attendees valued networking opportunities</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Content Quality</span>
                        <p className="text-sm text-gray-600">Speaker sessions rated 4.8/5 on average</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-indigo-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Venue Selection</span>
                        <p className="text-sm text-gray-600">88% satisfaction with venue choices</p>
                      </div>
                    </li>
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-xl p-6"
                >
                  <h4 className="text-lg font-semibold text-purple-900 mb-3">Improvement Areas</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Registration Process</span>
                        <p className="text-sm text-gray-600">Streamline check-in for large events</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Mobile App Experience</span>
                        <p className="text-sm text-gray-600">Enhanced features requested by 45% of users</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Follow-up Communication</span>
                        <p className="text-sm text-gray-600">More post-event resources needed</p>
                      </div>
                    </li>
                  </ul>
                </motion.div>
              </div>

              {/* Engagement Metrics */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Engagement Metrics</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Clock className="h-8 w-8 text-indigo-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">6.2h</div>
                    <div className="text-sm text-gray-600">Avg. Event Duration</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Users className="h-8 w-8 text-purple-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">84%</div>
                    <div className="text-sm text-gray-600">Session Attendance</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <FileText className="h-8 w-8 text-pink-600 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">76%</div>
                    <div className="text-sm text-gray-600">Survey Response Rate</div>
                  </div>
                  <div className="text-center p-4 bg-gray-50 rounded-lg">
                    <Star className="h-8 w-8 text-indigo-500 mx-auto mb-2" />
                    <div className="text-2xl font-bold text-gray-900">93%</div>
                    <div className="text-sm text-gray-600">Would Recommend</div>
                  </div>
                </div>
              </motion.div>
            </>
          )}

          {selectedMetric === "ai" && (
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              {/* AI Chat Header */}
              <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-2xl p-6 border border-indigo-100">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
                    <Sparkles className="h-6 w-6 text-indigo-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-gray-900">AI Event Analytics Assistant</h3>
                    <p className="text-sm text-gray-600">
                      Ask questions about your event data and get instant insights
                    </p>
                  </div>
                </div>

                {/* Demo Prompts */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-700">Try these example queries:</p>
                  {aiChatPrompts.map((prompt) => (
                    <button
                      key={prompt.id}
                      onClick={() => handleAIPromptClick(prompt.id)}
                      className={`w-full text-left p-4 rounded-xl border-2 transition-all ${
                        selectedAIPrompt === prompt.id
                          ? "bg-white border-indigo-300 shadow-md"
                          : "bg-white/50 border-gray-200 hover:border-indigo-200 hover:bg-white"
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-8 h-8 rounded-full flex items-center justify-center ${
                            selectedAIPrompt === prompt.id ? "bg-indigo-100" : "bg-gray-100"
                          }`}
                        >
                          {prompt.icon}
                        </div>
                        <span className="font-medium text-gray-800">{prompt.prompt}</span>
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {/* AI Response */}
              {selectedAIPrompt && showAIResponse && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  className="bg-white rounded-2xl shadow-lg p-6"
                >
                  {(() => {
                    const selectedPromptData = aiChatPrompts.find((p) => p.id === selectedAIPrompt);
                    if (!selectedPromptData) return null;

                    return (
                      <>
                        <div className="flex items-center gap-2 mb-4">
                          <MessageSquare className="h-5 w-5 text-indigo-600" />
                          <h4 className="font-semibold text-gray-900">Analysis Results</h4>
                        </div>

                        <div className="bg-indigo-50 rounded-lg p-4 mb-6">
                          <p className="text-gray-700 font-medium">
                            {streamedText}
                            {isStreaming && <span className="inline-block w-2 h-4 bg-indigo-600 ml-1 animate-pulse" />}
                          </p>
                        </div>

                        {!isStreaming && (
                          <motion.div
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.3 }}
                          >
                            <div className="overflow-x-auto">
                              <table className="w-full">
                                <thead>
                                  <tr className="border-b-2 border-gray-200">
                                    {Object.keys(selectedPromptData.response.data[0]).map((key) => (
                                      <th
                                        key={key}
                                        className="text-left py-3 px-4 text-sm font-semibold text-gray-700 capitalize"
                                      >
                                        {key.replace(/_/g, " ")}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody>
                                  {selectedPromptData.response.data.map((row, index) => (
                                    <motion.tr
                                      key={index}
                                      className="border-b border-gray-100 hover:bg-gray-50 transition-colors"
                                      initial={{ opacity: 0, x: -20 }}
                                      animate={{ opacity: 1, x: 0 }}
                                      transition={{ delay: index * 0.05 }}
                                    >
                                      {Object.entries(row).map(([key, value], cellIndex) => (
                                        <td key={cellIndex} className="py-3 px-4">
                                          {key === "count" || key === "percentage" ? (
                                            <span className="font-medium text-gray-900">{value}</span>
                                          ) : key === "status" ? (
                                            <span className="inline-flex px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">
                                              {value}
                                            </span>
                                          ) : (
                                            <span className="text-gray-700">{value}</span>
                                          )}
                                        </td>
                                      ))}
                                    </motion.tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>

                            <motion.div
                              className="mt-6 p-4 bg-gray-50 rounded-lg"
                              initial={{ opacity: 0 }}
                              animate={{ opacity: 1 }}
                              transition={{ delay: 0.5 }}
                            >
                              <p className="text-xs text-gray-500">
                                <span className="font-medium">AI Analysis:</span> This data was generated from your
                                event registration forms and attendee profiles. Last updated:{" "}
                                {new Date().toLocaleDateString()}
                              </p>
                            </motion.div>
                          </motion.div>
                        )}
                      </>
                    );
                  })()}
                </motion.div>
              )}

              {/* Additional Insights Cards */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-indigo-50 to-purple-100 rounded-xl p-6"
                >
                  <h4 className="text-lg font-semibold text-indigo-900 mb-3">Quick Stats</h4>
                  <ul className="space-y-3">
                    <li className="flex justify-between items-center">
                      <span className="text-gray-700">Response Rate</span>
                      <span className="font-bold text-indigo-600">89%</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="text-gray-700">Special Requests</span>
                      <span className="font-bold text-indigo-600">27</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="text-gray-700">Dietary Restrictions</span>
                      <span className="font-bold text-indigo-600">42</span>
                    </li>
                    <li className="flex justify-between items-center">
                      <span className="text-gray-700">First-Time Attendees</span>
                      <span className="font-bold text-indigo-600">38%</span>
                    </li>
                  </ul>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-xl p-6"
                >
                  <h4 className="text-lg font-semibold text-purple-900 mb-3">AI Recommendations</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Increase vegetarian options</span>
                        <p className="text-sm text-gray-600">18 attendees require vegetarian meals</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Add more networking time</span>
                        <p className="text-sm text-gray-600">85% satisfaction with networking</p>
                      </div>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="text-purple-600 mt-1">•</span>
                      <div>
                        <span className="font-medium text-gray-900">Consider accessibility needs</span>
                        <p className="text-sm text-gray-600">8 wheelchair accessibility requests</p>
                      </div>
                    </li>
                  </ul>
                </motion.div>
              </div>
            </motion.div>
          )}

          {selectedMetric === "roi" && (
            <>
              {/* ROI Overview */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6 mb-8"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Return on Investment Analysis</h3>
                <ResponsiveContainer width="100%" height={350}>
                  <BarChart
                    data={mockROIData}
                    margin={{
                      top: 20,
                      right: 30,
                      left: 20,
                      bottom: 40,
                    }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="event"
                      interval={0}
                      height={60}
                      textAnchor="end"
                      tick={{ fontSize: 15 }}
                      angle={-45}
                      tickMargin={10}
                      dx={-10}
                      dy={0}
                    />
                    <YAxis tick={{ fontSize: 15 }} tickFormatter={(value) => `$${value / 1000}K`} />
                    <Tooltip formatter={(value) => `$${((value as number) / 1000).toFixed(1)}K`} />
                    <Legend
                      verticalAlign="bottom"
                      height={36}
                      wrapperStyle={{
                        bottom: -30,
                        fontSize: "15px",
                      }}
                    />
                    <Bar dataKey="investment" fill="#C7D2FE" name="Investment" />
                    <Bar dataKey="return" fill="#10B981" name="Return" />
                  </BarChart>
                </ResponsiveContainer>
              </motion.div>

              {/* ROI Metrics */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-br from-purple-50 to-pink-100 rounded-xl p-6"
                >
                  <DollarSign className="h-8 w-8 text-green-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Average ROI</h4>
                  <div className="text-3xl font-bold text-gray-900">198%</div>
                  <p className="text-sm text-gray-600 mt-2">Average return across all events</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.1 }}
                  className="bg-gradient-to-br from-indigo-50 to-purple-100 rounded-xl p-6"
                >
                  <TrendingUp className="h-8 w-8 text-indigo-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Pipeline Generated</h4>
                  <div className="text-3xl font-bold text-gray-900">$4.2M</div>
                  <p className="text-sm text-gray-600 mt-2">From event-sourced leads</p>
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2 }}
                  className="bg-gradient-to-br from-pink-50 to-purple-100 rounded-xl p-6"
                >
                  <Users className="h-8 w-8 text-pink-600 mb-3" />
                  <h4 className="text-sm font-medium text-gray-600 mb-1">Cost per Lead</h4>
                  <div className="text-3xl font-bold text-gray-900">$127</div>
                  <p className="text-sm text-gray-600 mt-2">32% below industry average</p>
                </motion.div>
              </div>

              {/* ROI Details Table */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white rounded-xl shadow-sm p-6"
              >
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Event ROI Breakdown</h3>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-gray-200">
                        <th className="text-left py-3 px-4 text-sm font-medium text-gray-700">Event</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Investment</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">Revenue Generated</th>
                        <th className="text-right py-3 px-4 text-sm font-medium text-gray-700">ROI %</th>
                        <th className="text-center py-3 px-4 text-sm font-medium text-gray-700">Performance</th>
                      </tr>
                    </thead>
                    <tbody>
                      {mockROIData.map((event, index) => (
                        <tr key={index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-4 font-medium text-gray-900">{event.event}</td>
                          <td className="py-3 px-4 text-right text-gray-600">${event.investment.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right text-gray-600">${event.return.toLocaleString()}</td>
                          <td className="py-3 px-4 text-right font-medium text-gray-900">{event.roi}%</td>
                          <td className="py-3 px-4">
                            <div className="flex justify-center">
                              <div className="w-24 bg-gray-200 rounded-full h-2">
                                <div
                                  className={`h-2 rounded-full ${
                                    event.roi > 200
                                      ? "bg-green-600"
                                      : event.roi > 100
                                        ? "bg-indigo-600"
                                        : "bg-purple-400"
                                  }`}
                                  style={{ width: `${Math.min(event.roi / 3, 100)}%` }}
                                ></div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </motion.div>
            </>
          )}
        </div>
      )}
      <CTA />
    </div>
  );
}
